import m from "mithril";
import { getUserData, getMithril, postMithril, deleteMithril } from "../utils.js";
import { getTerm, getNomenclature, getDefaultTerms } from "../js/nomenclature.js";
import { showToast } from "../js/toaster.js";

const NomenclatureSettings = {
    oninit: async function (vnode) {
        this.userData = {};
        this.isLoading = true;
        this.isSaving = false;
        this.nomenclature = getNomenclature(); // Current merged nomenclature
        this.defaultTerms = getDefaultTerms(); // Pure defaults for reference
        this.customTerms = {};
        this.selectedCategory = 'navigation';
        this.searchTerm = '';

        try {
            const userData = await getUserData();
            this.userData = userData || {};

            // Ensure we have an entity ID to work with
            this.entityId = this.userData.subunit_id || this.userData.entityId;
            if (this.userData.role === 'manajer' && !this.entityId) {
                // Try to find entity ID from local storage as fallback
                this.entityId = localStorage.getItem('subunit_id') || localStorage.getItem('entityId');
            }

            this.loadData();
        } catch (error) {
            console.error('Failed to load user data:', error);
            showToast('Gagal memuat data pengguna', 'error');
            this.isLoading = false;
            m.redraw();
        }
    },

    loadData: function () {
        if (this.entityId) {
            this.loadCustomTerms();
        } else {
            // Delay toast to ensure view is mounted
            setTimeout(() => {
                showToast('Akun Anda tidak terhubung dengan unit usaha spesifik. Pengaturan tidak dapat disimpan.', 'warning', 5000);
            }, 500);
        }
    },

    loadCustomTerms: async function () {
        this.isLoading = true;
        m.redraw();

        try {
            const entityId = this.entityId;

            if (!entityId) {
                console.warn('No entity ID available for loading custom terms');
                return;
            }

            const result = await getMithril(`/api/nomenclature/subunit/${entityId}`, { component: this });
            if (result.success) {
                // Backend returns an object { key: value }, so we can assign it directly
                // or ensure it's treated as such.
                this.customTerms = result.data || {};
            } else {
                throw new Error(result.error || 'Failed to load custom terms');
            }
        } catch (error) {
            console.error('Failed to load custom terms:', error);
            showToast('Failed to load current terminology settings', 'error');
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    saveTerm: async function (termKey, newValue) {
        if (!newValue.trim()) {
            showToast('Term value cannot be empty', 'warning');
            return;
        }

        this.isSaving = true;
        m.redraw();

        try {
            const entityId = this.entityId;

            if (!entityId) {
                throw new Error('Informasi unit usaha tidak ditemukan. Silakan login ulang.');
            }

            // Prepare the full preferences object (backend overwrites all)
            const preferences = { ...this.customTerms };
            preferences[termKey] = newValue.trim();

            console.log('Sending POST request to save term:', { subunitId: parseInt(entityId), preferences });

            const result = await postMithril('/api/nomenclature', {
                subunitId: parseInt(entityId),
                preferences
            }, { component: this });

            console.log('POST response data:', result);

            if (result.success) {
                this.customTerms[termKey] = newValue.trim();
                showToast(`✅ Terminologi "${termKey}" berhasil diperbarui menjadi "${newValue.trim()}"`, 'success', 7000);
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                throw new Error(result.error || 'Failed to save term');
            }
        } catch (error) {
            console.error('Failed to save term:', error);
            showToast('Failed to save terminology: ' + error.message, 'error');
        } finally {
            this.isSaving = false;
            m.redraw();
        }
    },

    resetTerm: async function (termKey) {
        this.isSaving = true;
        m.redraw();

        try {
            const entityId = this.entityId;

            if (!entityId) {
                throw new Error('Informasi unit usaha tidak ditemukan');
            }

            // Create a copy of preferences without the term to be reset
            const preferences = { ...this.customTerms };
            delete preferences[termKey];

            // Use POST to update (rewrite) preferences without this key
            const result = await postMithril('/api/nomenclature', {
                subunitId: parseInt(entityId),
                preferences
            }, { component: this });

            if (result.success) {
                delete this.customTerms[termKey];
                showToast(`🔄 Terminologi "${termKey}" berhasil dikembalikan ke default`, 'success', 7000);
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                throw new Error(result.error || 'Failed to reset term');
            }
        } catch (error) {
            console.error('Failed to reset term:', error);
            showToast('Failed to reset terminology: ' + error.message, 'error');
        } finally {
            this.isSaving = false;
            m.redraw();
        }
    },

    getFilteredTerms: function () {
        const categoryData = this.defaultTerms[this.selectedCategory] || {};
        const terms = Object.keys(categoryData);

        if (!this.searchTerm.trim()) {
            return terms;
        }

        return terms.filter(key =>
            key.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            categoryData[key].toLowerCase().includes(this.searchTerm.toLowerCase())
        );
    },

    view: function (vnode) {
        const entityType = this.userData.entity_type || this.userData.entityType || 'unit_usaha';
        const entityName = entityType === 'koperasi' ? 'Koperasi' : 'Unit Usaha';

        return m("div", { class: "p-6" }, [
            m("div", { class: "mb-6" }, [
                m("h2", { class: "text-2xl font-bold text-gray-800 dark:text-white" }, "Pengaturan Terminologi"),
                m("p", { class: "text-gray-600 dark:text-gray-300" }, `Kelola terminologi untuk ${entityName}`)
            ]),

            // Loading indicator
            this.isLoading && m("div", { class: "text-center py-4" }, [
                m("i", { class: "ri-loader-4-line animate-spin text-2xl" }),
                m("p", { class: "mt-2" }, "Memuat pengaturan terminologi...")
            ]),

            !this.isLoading && m("div", { class: "space-y-6" }, [
                // Category selector
                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6" }, [
                    m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-4" }, "Kategori Terminologi"),
                    m("div", { class: "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4" }, [
                        ['navigation', 'Navigasi'],
                        ['dashboard', 'Dashboard'],
                        ['common', 'Umum'],
                        ['fields', 'Field'],
                        ['titles', 'Judul'],
                        ['descriptions', 'Deskripsi'],
                        ['actions', 'Aksi'],
                        ['tableHeaders', 'Tabel']
                    ].map(([key, label]) =>
                        m("button", {
                            class: `p-3 rounded-lg border-2 transition-colors ${this.selectedCategory === key
                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                                : 'border-gray-200 dark:border-gray-600 hover:border-orange-300 text-gray-700 dark:text-gray-300'
                                }`,
                            onclick: () => this.selectedCategory = key
                        }, label)
                    ))
                ]),

                // Search
                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6" }, [
                    m("div", { class: "flex items-center space-x-4" }, [
                        m("i", { class: "ri-search-line text-gray-400" }),
                        m("input", {
                            type: "text",
                            placeholder: "Cari terminologi...",
                            class: "flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white",
                            value: this.searchTerm,
                            oninput: (e) => this.searchTerm = e.target.value
                        })
                    ])
                ]),

                // Terms list
                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow p-6" }, [
                    m("h3", { class: "text-lg font-semibold text-gray-800 dark:text-white mb-4" },
                        `Terminologi ${this.selectedCategory.charAt(0).toUpperCase() + this.selectedCategory.slice(1)}`
                    ),

                    this.getFilteredTerms().length === 0
                        ? m("p", { class: "text-gray-500 dark:text-gray-400 text-center py-8" }, "Tidak ada terminologi ditemukan")
                        : m("div", { class: "space-y-4" },
                            this.getFilteredTerms().map(termKey => {
                                const defaultValue = this.defaultTerms[this.selectedCategory]?.[termKey] || '';
                                const customValue = this.customTerms[`${this.selectedCategory}.${termKey}`];
                                const currentValue = customValue || defaultValue;

                                return m("div", {
                                    class: "border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                                }, [
                                    m("div", { class: "flex items-center justify-between mb-2" }, [
                                        m("label", {
                                            class: "font-medium text-gray-700 dark:text-gray-300",
                                            for: `term-${termKey}`
                                        }, termKey),
                                        customValue && customValue !== defaultValue && m("span", {
                                            class: "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                        }, "Disesuaikan")
                                    ]),

                                    m("div", { class: "flex items-center space-x-3" }, [
                                        m("input", {
                                            id: `term-${termKey}`,
                                            type: "text",
                                            class: "flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white",
                                            value: currentValue,
                                            placeholder: defaultValue,
                                            oninput: (e) => {
                                                // Update local state for immediate UI feedback
                                                if (e.target.value !== defaultValue) {
                                                    this.customTerms[`${this.selectedCategory}.${termKey}`] = e.target.value;
                                                } else {
                                                    delete this.customTerms[`${this.selectedCategory}.${termKey}`];
                                                }
                                            }
                                        }),

                                        m("button", {
                                            class: `px-4 py-2 rounded-lg transition-colors ${customValue && customValue !== defaultValue
                                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-300'
                                                }`,
                                            disabled: this.isSaving || !customValue || customValue === defaultValue,
                                            onclick: () => {
                                                console.log('Save button clicked for:', `${this.selectedCategory}.${termKey}`, 'value:', currentValue, 'customValue:', customValue, 'defaultValue:', defaultValue);
                                                this.saveTerm(`${this.selectedCategory}.${termKey}`, currentValue);
                                            }
                                        }, [
                                            this.isSaving ? m("i", { class: "ri-loader-4-line animate-spin" }) : m("i", { class: "ri-save-line mr-1" }),
                                            "Simpan"
                                        ]),

                                        customValue && m("button", {
                                            class: "px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors",
                                            disabled: this.isSaving,
                                            onclick: () => this.resetTerm(`${this.selectedCategory}.${termKey}`)
                                        }, [
                                            this.isSaving ? m("i", { class: "ri-loader-4-line animate-spin" }) : m("i", { class: "ri-refresh-line mr-1" }),
                                            "Reset"
                                        ])
                                    ]),

                                    m("div", { class: "mt-2 text-sm text-gray-500 dark:text-gray-400" }, [
                                        m("span", "Default: "),
                                        m("span", { class: "font-mono" }, defaultValue)
                                    ])
                                ]);
                            })
                        )
                ])
            ])
        ]);
    }
};

export default NomenclatureSettings;